import { useEffect, useRef, useState, useCallback } from 'react'
import { useScroll } from '../contexts/ScrollContext'
import styles from './HeroScrollSequence.module.css'

// ─── CONFIGURATION ────────────────────────────────────────────────────────────
//  Adjust FRAME_URL to match your actual exported filenames.
//  Default expects:  /frames/frame_0001.jpg  →  /frames/frame_0240.jpg
const TOTAL_FRAMES              = 240
const SCROLL_DISTANCE_VH        = 230    // vh – total scroll distance (desktop)
const SCROLL_DISTANCE_VH_MOBILE = 160    // vh – total scroll distance (mobile, ≤ 768px)
const TEXT_FADE_END       = 0.18         // progress (0-1) at which hero text is fully gone
const INDICATOR_FADE_END  = 0.08         // progress at which scroll indicator is fully gone
const STAGE_1_FRAMES      = 30           // frames fired immediately; rest yielded via setTimeout

const FRAME_URL = (index: number) =>
  `/frames/hero-1/ezgif-frame-${String(index + 1).padStart(3, '0')}.webp`
// ─────────────────────────────────────────────────────────────────────────────

// ─── DEBUG LOGGING ────────────────────────────────────────────────────────────
//  Set to true to enable verbose console logging for stutter diagnosis.
//  Topics logged:
//    [metrics]  containerTop / scrollableRange every time they change
//    [scroll]   smoothY, raw progress, targetFrame each tick
//    [stutter]  large frame jumps (≥ 5 frames) or containerTop changes mid-scroll
//    [draw]     fallback draws (when requested frame isn't loaded yet)
//    [load]     frame load completions
const DEBUG_SCROLL = false
// ─────────────────────────────────────────────────────────────────────────────

// ── Hero content – defined outside component so it never gets remounted ───────
interface HeroContentProps {
  onConsult: () => void
  onSeeHow: () => void
}

const HeroContent = ({ onConsult, onSeeHow }: HeroContentProps) => (
  <div className="container">
    <div className={styles.textContainer}>
      <h1 className={styles.headline}>
        <span className={styles.headlineLine}>Senior Engineer Level</span>
        
        <span className={`${styles.headlineLine} ${styles.highlightLine}`}>
          AI-Assisted Development
        </span>
      </h1>

      <p className={styles.subheading}>
        Not a generic assistant. An editor that knows your codebase —
        its history, its decisions, its people.
      </p>

      <div className={styles.ctaContainer}>
        <button
          className={`${styles.ctaButton} ${styles.primary}`}
          onClick={onConsult}
        >
          Join the Waitlist
        </button>
        <button
          className={`${styles.ctaButton} ${styles.secondary}`}
          onClick={onSeeHow}
        >
          See How It Works
          <svg
            className={styles.arrow}
            width="20" height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M10 4V16M10 16L4 10M10 16L16 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className={styles.trustBadge}>
        <span className={styles.badgeNumber}>Box</span>
        <span className={styles.badgeText}>AI-assisted editor</span>
      </div>
    </div>
  </div>
)

const HeroScrollSequence = () => {
  // ── Refs (DOM + state that should NOT trigger re-renders on scroll) ──────────
  const containerRef        = useRef<HTMLDivElement>(null)
  const pinnedRef           = useRef<HTMLDivElement>(null)
  const canvasRef           = useRef<HTMLCanvasElement>(null)
  const contentRef          = useRef<HTMLDivElement>(null)
  const scrollIndicatorRef  = useRef<HTMLDivElement>(null)
  const frameCounterRef     = useRef<HTMLSpanElement>(null)
  const ctxRef              = useRef<CanvasRenderingContext2D | null>(null)
  const imagesRef           = useRef<(HTMLImageElement | null)[]>([])
  const metricsRef          = useRef({ containerTop: 0, scrollableRange: 0 })
  const currentFrameRef     = useRef(-1)  // index of the frame that was actually drawn (may be a fallback)
  const intendedFrameRef    = useRef(-1)  // index the scroll position is targeting (may not be loaded yet)
  const displayedFrameRef   = useRef(-1)  // smoothed display frame (advances toward targetFrame at MAX_FRAMES_PER_TICK/tick)
  const loadedCountRef      = useRef(0)
  // Stable ref to drawFrame so effects that call it don't need it as a dep
  // (avoiding dep-driven re-registration of the scroll subscriber, which was
  // causing double-fires).
  const drawFrameRef        = useRef<(index: number) => void>(() => {})

  // ── React state (only what needs to drive JSX changes) ───────────────────────
  const [loadProgress,    setLoadProgress]    = useState(0)      // 0 → 1
  const [firstFrameReady, setFirstFrameReady] = useState(false)

  // Pick scroll distance based on device width — computed once on mount,
  // matching the same ≤768px breakpoint SmoothScroll uses for isMobile.
  const scrollDistanceVh = typeof window !== 'undefined' && window.innerWidth <= 768
    ? SCROLL_DISTANCE_VH_MOBILE
    : SCROLL_DISTANCE_VH

  // subscribe gives us frame-perfect scroll updates from SmoothScroll's RAF tick
  const { subscribe, isSmooth } = useScroll()
  // Stable ref so the subscribe callback can read the current value without
  // being recreated when isSmooth changes (it never does after mount, but
  // using a ref keeps the pattern consistent with the other DOM refs).
  const isSmoothRef = useRef(isSmooth)

  // ── Draw a frame to the canvas (object-fit: cover + nearest-frame fallback) ──
  //
  //  When the requested frame isn't ready yet, scan backward to the nearest one
  //  that is. This produces a "snap" on fast scrolls rather than a frozen blank.
  //  currentFrameRef tracks the frame we *actually drew* (possibly a fallback),
  //  not the intent — so the scroll callback keeps retrying the true target each
  //  tick, and the onload trigger below can fire a redraw when a gap fills.
  //
  //  We draw directly from the HTMLImageElement rather than an ImageBitmap.
  //  ctx.drawImage(HTMLImageElement) uses the browser's high-quality scaling
  //  pipeline. ImageBitmap is pre-rasterized at natural size and re-scaled by
  //  the GPU at draw time, which produces lower-quality results when the canvas
  //  is a different size than the source image (which is always the case here).
  // Keep drawFrameRef in sync so callers don't need drawFrame as a dep.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const drawFrame = useCallback((index: number) => {
    const canvas = canvasRef.current
    const ctx    = ctxRef.current
    if (!canvas || !ctx) return

    // Scan backward for the nearest fully decoded HTMLImageElement
    let actualIndex = index
    while (actualIndex >= 0) {
      const img = imagesRef.current[actualIndex]
      if (img != null && img.complete && img.naturalWidth > 0) break
      actualIndex--
    }
    if (actualIndex < 0) {
      if (DEBUG_SCROLL) console.log('[draw] nothing loaded yet, index=', index)
      return   // nothing loaded yet
    }

    if (DEBUG_SCROLL && actualIndex !== index) {
      console.log(`[draw] fallback: wanted frame ${index}, drew frame ${actualIndex} (gap of ${index - actualIndex})`)
    }

    const img = imagesRef.current[actualIndex]!

    const cw = canvas.width
    const ch = canvas.height
    const iw = img.naturalWidth
    const ih = img.naturalHeight

    // Cover: scale so the smaller dimension fills the canvas, crop the other
    let sx = 0, sy = 0, sw = iw, sh = ih
    if (iw / ih > cw / ch) {
      // Image is wider → crop left/right
      sw = ih * (cw / ch)
      sx = (iw - sw) / 2
    } else {
      // Image is taller → crop top/bottom
      sh = iw * (ch / cw)
      sy = (ih - sh) / 2
    }

    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch)
    currentFrameRef.current = actualIndex  // record what was drawn, not the intent
  }, [])
  // Always keep the ref pointing at the latest drawFrame identity
  drawFrameRef.current = drawFrame

  // ── Set canvas pixel dimensions and context on mount / resize ────────────────
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Track the last committed width so we can ignore mobile address-bar
    // resize events (height-only) that would otherwise clear the canvas buffer
    // and cause the image to visibly jump/resize mid-scroll.
    let lastWidth = window.innerWidth

    const setSize = () => {
      const newWidth = window.innerWidth
      // On mobile (no smooth scroll wrapper), skip resize events where only the
      // height changed — these are caused by the address bar appearing/hiding.
      // Real resizes (orientation change, window resize) always change the width.
      if (!isSmoothRef.current && newWidth === lastWidth) return
      lastWidth = newWidth

      canvas.width  = newWidth
      canvas.height = window.innerHeight
      ctxRef.current = canvas.getContext('2d')
      // Redraw current frame after resize
      if (currentFrameRef.current >= 0) drawFrameRef.current(currentFrameRef.current)
    }

    setSize()
    window.addEventListener('resize', setSize)
    return () => window.removeEventListener('resize', setSize)
  }, [])  // no deps — drawFrameRef is always current without being a dep

  // ── Cache container metrics (avoids forced reflow inside the RAF callback) ───
  //
  //  This must run frequently enough to catch SmoothScroll's late height
  //  calculations (it fires ResizeObserver callbacks at 0/100/300/500 ms after
  //  mount, which change the spacer height and thus containerTop).  We use:
  //    • A ResizeObserver on the container itself (catches layout shifts)
  //    • Several setTimeout passes after mount (catches the spacer settling)
  //    • The existing resize listener (catches orientation/window changes)
  useEffect(() => {
    let lastWidth = window.innerWidth

    const update = () => {
      const c = containerRef.current
      if (!c) return
      const newWidth = window.innerWidth
      // On mobile skip height-only resize events (address bar show/hide)
      if (!isSmoothRef.current && newWidth === lastWidth) return
      lastWidth = newWidth

      // On desktop, the container lives inside a position:fixed wrapper with
      // transform:translateY(-smoothY).  offsetTop walks up to that wrapper,
      // so it gives the container's position *within* the wrapper — which is
      // exactly what we want for the containerTop calculation against smoothY.
      // We re-read it here; a ResizeObserver also calls us when layout shifts.
      const prev = metricsRef.current
      const next = {
        containerTop:    c.offsetTop,
        scrollableRange: Math.max(1, c.offsetHeight - window.innerHeight),
      }
      if (DEBUG_SCROLL) {
        const topChanged   = Math.abs(next.containerTop - prev.containerTop) > 0.5
        const rangeChanged = Math.abs(next.scrollableRange - prev.scrollableRange) > 0.5
        if (topChanged || rangeChanged) {
          console.log(
            '[metrics] updated',
            `containerTop: ${prev.containerTop.toFixed(1)} → ${next.containerTop.toFixed(1)}`,
            `scrollableRange: ${prev.scrollableRange.toFixed(1)} → ${next.scrollableRange.toFixed(1)}`,
            new Error('stack').stack?.split('\n')[2]?.trim() ?? '',
          )
        }
      }
      metricsRef.current = next
    }

    update()
    window.addEventListener('resize', update)

    // Re-measure after SmoothScroll's own delayed height recalculations.
    // SmoothScroll fires its ResizeObserver at 0/100/300/500 ms, which shifts
    // the spacer div — and therefore containerTop — after initial mount.
    const timers = [0, 50, 100, 200, 300, 500, 800].map(d =>
      setTimeout(update, d)
    )

    // Also observe the container element itself for any size/position changes
    const ro = new ResizeObserver(update)
    if (containerRef.current) ro.observe(containerRef.current)

    return () => {
      window.removeEventListener('resize', update)
      timers.forEach(clearTimeout)
      ro.disconnect()
    }
  }, [])

  // ── Mobile: apply CSS sticky so the browser compositor owns the pin ─────────
  //
  //  On desktop, SmoothScroll wraps everything in a position:fixed element
  //  shifted by -smoothY, so we need a JS translateY to counteract that shift.
  //  On mobile, SmoothScroll renders children in normal document flow with NO
  //  transform wrapper. Applying a JS translateY to counteract native scroll is
  //  inherently racy — the compositor scrolls before the main-thread JS lands,
  //  producing 1-2 frame lag on every frame (the core mobile jitter).
  //
  //  CSS `position:sticky` is compositor-owned: the browser pins the element
  //  at zero cost, perfectly V-sync-aligned, with zero JS involvement.
  useEffect(() => {
    const pinned = pinnedRef.current
    if (!pinned) return
    if (!isSmoothRef.current) {
      // Mobile: let the browser pin natively
      pinned.style.position  = 'sticky'
      pinned.style.top       = '0'
      pinned.style.transform = '' // ensure no leftover JS transform
    }
    // Desktop: styles are left entirely to the CSS module
  }, []) // runs once on mount; isSmooth never changes after mount

  // ── Staged frame preload ──────────────────────────────────────────────────────
  //
  //  Firing all 240 requests simultaneously creates a large burst that starves
  //  the early frames the user sees first.  Instead:
  //    Stage 1 – first STAGE_1_FRAMES fire immediately (fills the visible window)
  //    Stage 2 – the rest are kicked off after a setTimeout(0) yield so the
  //              browser can service the stage-1 responses before queuing more.
  useEffect(() => {
    let mounted = true

    // Reset all frame state so stale data from a previous mount can't bleed in
    imagesRef.current         = new Array(TOTAL_FRAMES).fill(null)
    loadedCountRef.current    = 0
    currentFrameRef.current   = -1
    intendedFrameRef.current  = -1
    displayedFrameRef.current = -1

    const kickOffLoad = (i: number) => {
      const img = new Image()
      imagesRef.current[i] = img
      img.src = FRAME_URL(i)

      img.onload = () => {
        if (!mounted) return

        loadedCountRef.current++

        if (DEBUG_SCROLL && (i < 5 || i % 30 === 0)) {
          console.log(`[load] frame ${i} ready, total loaded: ${loadedCountRef.current}`)
        }

        // Update progress bar every 5 frames to avoid excessive re-renders
        if (loadedCountRef.current % 5 === 0 || loadedCountRef.current === TOTAL_FRAMES) {
          setLoadProgress(loadedCountRef.current / TOTAL_FRAMES)
        }

        // First frame ready → show canvas, hide loader
        if (i === 0) {
          if (DEBUG_SCROLL) console.log('[load] frame 0 ready, metrics at this point:', { ...metricsRef.current })
          setFirstFrameReady(true)
          drawFrameRef.current(0)
        }

        // If this newly ready frame is closer to the intended scroll target than
        // what's currently on screen, trigger a redraw so the canvas catches up
        // without waiting for the next scroll event (important when user is still).
        const intended = intendedFrameRef.current
        if (intended >= 0 && i <= intended && i > currentFrameRef.current) {
          drawFrameRef.current(intended)
        }
      }

      img.onerror = () => {
        if (!mounted) return
        // Count failed loads toward progress so the bar can always reach 100 %
        loadedCountRef.current++
        if (loadedCountRef.current % 5 === 0 || loadedCountRef.current === TOTAL_FRAMES) {
          setLoadProgress(loadedCountRef.current / TOTAL_FRAMES)
        }
      }
    }

    // Stage 1 – critical early frames
    for (let i = 0; i < Math.min(STAGE_1_FRAMES, TOTAL_FRAMES); i++) kickOffLoad(i)

    // Stage 2 – rest of the sequence, yielded so stage-1 responses get priority
    const timerId = setTimeout(() => {
      for (let i = STAGE_1_FRAMES; i < TOTAL_FRAMES; i++) kickOffLoad(i)
    }, 0)

    return () => {
      mounted = false
      clearTimeout(timerId)
    }
  }, [])  // no deps — drawFrameRef is always current without being a dep

  // ── Scroll-driven animation via subscription ──────────────────────────────────
  //
  //  Previously this used useEffect([scrollY]) where scrollY came from React
  //  context state updated inside the RAF loop. That caused:
  //    RAF tick → setCurrentScroll → React re-render → useEffect → DOM write
  //  The re-render latency meant the wrapper translateY and the pinned-element
  //  compensating translateY landed in DIFFERENT paint frames → jitter / stutter.
  //
  //  Now we subscribe directly: the callback fires INSIDE SmoothScroll's RAF
  //  tick, AFTER the wrapper transform is applied, so both DOM writes land in
  //  the same browser paint. No React re-renders per frame.
  useEffect(() => {
    if (DEBUG_SCROLL) console.log('[subscribe] registering scroll subscriber')
    // Read DOM refs inside the callback, not at effect-registration time.
    const onScroll = (smoothY: number) => {
      const pinned         = pinnedRef.current
      const content        = contentRef.current
      const scrollIndicator = scrollIndicatorRef.current
      const frameCounter   = frameCounterRef.current

      if (!pinned) return

      // Read from cache — no DOM access, no forced reflow
      const { containerTop, scrollableRange } = metricsRef.current

      // ── Progress 0 → 1 across the sequence
      const raw      = (smoothY - containerTop) / scrollableRange
      const progress = Math.max(0, Math.min(1, raw))

      // ── Pre-compute all values before any DOM writes ─────────────────────
      const rawFrame         = Math.round(progress * (TOTAL_FRAMES - 1))
      // Track intent so the onload trigger can fire a redraw when a gap fills
      intendedFrameRef.current = rawFrame
      // Clamp to what's actually loaded: prevents advancing into an unloaded gap
      const maxLoadedFrame   = Math.max(0, loadedCountRef.current - 1)
      const targetFrame      = Math.min(rawFrame, maxLoadedFrame)
      const textOpacity      = Math.max(0, 1 - raw / TEXT_FADE_END)
      const indicatorOpacity = Math.max(0, 1 - raw / INDICATOR_FADE_END)

      if (DEBUG_SCROLL) {
        // Log every tick when inside the hero zone
        if (progress > 0 && progress < 1) {
          console.log(
            '[scroll]',
            `smoothY=${smoothY.toFixed(1)}`,
            `containerTop=${containerTop.toFixed(1)}`,
            `scrollableRange=${scrollableRange.toFixed(1)}`,
            `raw=${raw.toFixed(3)}`,
            `progress=${progress.toFixed(3)}`,
            `targetFrame=${targetFrame}`,
            `loaded=${loadedCountRef.current}`,
          )
        }
        // Alert if we're clamped to loaded frames
        if (rawFrame > maxLoadedFrame) {
          console.log(
            '[scroll] clamped: rawFrame', rawFrame, '> maxLoaded', maxLoadedFrame,
          )
        }
      }

      // ── Canvas frame — snap directly to targetFrame each tick ────────────
      //
      //  SmoothScroll's lerp already guarantees smooth motion; a per-tick
      //  step cap here only causes the sequence to trail behind the scroll
      //  position and accumulate lag during fast scrolls.  Snapping directly
      //  keeps the frame perfectly in sync with smoothY.
      //
      //  displayedFrameRef mirrors targetFrame every tick.
      //  currentFrameRef   = the frame actually on screen (may be a fallback).
      displayedFrameRef.current = targetFrame

      if (targetFrame !== currentFrameRef.current) drawFrameRef.current(targetFrame)

      // ── Desktop-only: manual sticky / pin ───────────────────────────────
      //    The SmoothScroll wrapper applies translateY(-smoothY) to the fixed
      //    container.  To keep the pinned element at viewport top we add back
      //    +smoothY — no clamping needed because the wrapper's own transform
      //    already constrains visible position.  Clamping to scrollableRange
      //    was causing the element to drift upward when smoothY exceeded the
      //    clamp value (e.g. at the bottom of the sequence).
      //    On mobile, `position:sticky` is applied via CSS (see effect above)
      //    and the compositor owns the pin — no JS transform needed or wanted.
      if (isSmoothRef.current) {
        pinned.style.transform = `translateY(${smoothY}px)`
      }

      // ── Hero text fade — opacity only, no translateY ────────────────────
      //    A translateY on .content during upward scroll caused a visible
      //    positional pop: as raw returned toward 0 the element snapped from
      //    a negative offset back to 0 while becoming visible again.  The
      //    CSS slideUp animation on the individual headline lines already
      //    handles the entry motion; the JS only needs to drive visibility.
      if (content) {
        content.style.cssText =
          `opacity:${textOpacity};` +
          `pointer-events:${textOpacity < 0.05 ? 'none' : 'auto'}`
      }

      // ── Scroll indicator fade ────────────────────────────────────────────
      if (scrollIndicator) {
        scrollIndicator.style.opacity = `${indicatorOpacity}`
      }

      // ── Subtle frame counter (high-tech feel) ────────────────────────────
      if (frameCounter) {
        frameCounter.textContent =
          `${String(rawFrame + 1).padStart(3, '0')} / ${TOTAL_FRAMES}`
      }
    }

    // subscribe returns the unsubscribe fn — perfect as useEffect cleanup
    return subscribe(onScroll)
  }, [subscribe])  // drawFrame removed — accessed via drawFrameRef, which is always current

  // ── Scroll to next section ───────────────────────────────────────────────────
  const scrollToContent = () => {
    const trustBar = document.getElementById('trust-bar')
    if (trustBar) window.scrollTo({ top: trustBar.offsetTop, behavior: 'smooth' })
  }

  const handleConsult = () => {
    const contactSection = document.getElementById('contact')
    if (contactSection) {
      const offset = 80
      const elementPosition = contactSection.getBoundingClientRect().top + window.scrollY
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' })
    }
  }

  // ── Scroll-driven canvas sequence (all screen sizes) ────────────────────────
  return (
    <div
      ref={containerRef}
      className={styles.container}
      style={{ height: `${scrollDistanceVh}vh` }}
    >
      {/* ── Pinned viewport frame (simulates position:sticky via manual transform) */}
      <div ref={pinnedRef} className={styles.pinned}>

        {/* Canvas: image sequence rendered here */}
        <canvas
          ref={canvasRef}
          className={styles.canvas}
          role="img"
          aria-label="Box Editor hero: AI-assisted development workflow animation"
        />

        {/* Gradient overlay – same tone as original hero */}
        <div className={styles.overlay} />

        {/* Loading overlay – shown until first frame is decoded */}
        <div className={`${styles.loaderOverlay} ${firstFrameReady ? styles.loaderHidden : ''}`}>
          <div className={styles.loaderTrack}>
            <div
              className={styles.loaderFill}
              style={{ width: `${loadProgress * 100}%` }}
            />
          </div>
          <span className={styles.loaderText}>
            {Math.round(loadProgress * 100)}%
          </span>
        </div>

        {/* Hero content – fades out as sequence progresses */}
        <div ref={contentRef} className={styles.content}>
          <HeroContent onConsult={handleConsult} onSeeHow={scrollToContent} />
        </div>

        {/* Scroll indicator */}
        <div ref={scrollIndicatorRef} className={styles.scrollIndicator}>
          <div className={styles.scrollLine} />
        </div>

        {/* Subtle frame counter – adds a high-tech diagnostic feel */}
        <div className={styles.frameCounter}>
          <span ref={frameCounterRef}>001 / {TOTAL_FRAMES}</span>
        </div>

        {/* Loading progress (persists after loader hides – fades on its own) */}
        {!firstFrameReady && (
          <div className={styles.loadingSubtext}>
            Loading sequence…
          </div>
        )}
      </div>
    </div>
  )
}

export default HeroScrollSequence
